import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, EyeOff } from 'lucide-react';

const PAD = 10;
const visible = (el) => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return false;
  const cs = getComputedStyle(el);
  return cs.visibility !== 'hidden' && cs.display !== 'none' && cs.opacity !== '0';
};

/** Dimmed page with a cut-out around the current step's element and a tooltip beside it. */
export function Spotlight({ tour, onClose }) {
  const steps = useMemo(() => tour.steps, [tour]);
  const [phase, setPhase] = useState('intro'); // intro | step
  const [i, setI] = useState(-1);
  const [rect, setRect] = useState(null);
  const [tick, setTick] = useState(0);

  // Find the next/previous step whose element is actually on the page
  const resolve = useCallback((from, dir) => {
    let j = from;
    while (j >= 0 && j < steps.length) {
      const el = document.querySelector(steps[j].target);
      if (visible(el)) return j;
      j += dir;
    }
    return -1;
  }, [steps]);

  const go = useCallback(async (from, dir) => {
    // run `before` hooks (e.g. spin the carousel) on candidates as we look for a visible target
    let j = from;
    while (j >= 0 && j < steps.length) {
      const s = steps[j];
      if (s.before) { s.before(); await new Promise((r) => setTimeout(r, 780)); }
      const el = document.querySelector(s.target);
      if (visible(el)) {
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        await new Promise((r) => setTimeout(r, 380));
        setI(j); setPhase('step');
        return;
      }
      j += dir;
    }
    if (dir > 0) onClose(); else { setPhase('intro'); setI(-1); }
  }, [steps, onClose]);

  const measure = useCallback(() => {
    if (phase !== 'step' || i < 0) return;
    const el = document.querySelector(steps[i].target);
    if (!visible(el)) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
  }, [phase, i, steps]);

  useLayoutEffect(() => { measure(); }, [measure, tick]);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener('resize', bump); window.addEventListener('scroll', bump, true);
    const id = setInterval(bump, 400);
    return () => { window.removeEventListener('resize', bump); window.removeEventListener('scroll', bump, true); clearInterval(id); };
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight' || e.key === 'Enter') go(i + 1, 1); if (e.key === 'ArrowLeft') go(i - 1, -1); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, i, onClose]);

  const remaining = useMemo(() => steps.filter((s) => visible(document.querySelector(s.target))).length, [steps, tick]);
  const doneCount = useMemo(() => steps.slice(0, i + 1).filter((s) => visible(document.querySelector(s.target))).length, [steps, i, tick]);

  // Tooltip placement: below the target if there is room, otherwise above; clamp horizontally.
  const tip = useMemo(() => {
    if (!rect) return null;
    const W = 360, H = 190, vw = window.innerWidth, vh = window.innerHeight;
    const below = rect.top + rect.height + H + 16 < vh;
    const top = below ? rect.top + rect.height + 14 : Math.max(12, rect.top - H - 14);
    const left = Math.min(Math.max(12, rect.left + rect.width / 2 - W / 2), vw - W - 12);
    return { top, left, width: W, arrow: below ? 'up' : 'down', arrowX: Math.min(Math.max(18, rect.left + rect.width / 2 - left), W - 18) };
  }, [rect]);

  const step = i >= 0 ? steps[i] : null;
  return createPortal(
    <div className="tour-root" data-testid="tour-overlay">
      {phase === 'intro' && (
        <div className="tour-dim tour-dim-full" onClick={() => onClose()}>
          <div className="tour-card tour-intro" onClick={(e) => e.stopPropagation()} data-testid="tour-intro">
            <div className="tour-eyebrow">Quick tour · {remaining} stops</div>
            <h3>{tour.title}</h3>
            <p>{tour.intro}</p>
            <div className="tour-actions">
              <button type="button" className="tour-btn tour-btn-primary" onClick={() => go(0, 1)} data-testid="tour-start">Show me around <ChevronRight size={16} /></button>
              <button type="button" className="tour-btn tour-btn-ghost" onClick={() => onClose()} data-testid="tour-skip">Not now</button>
              <button type="button" className="tour-btn tour-btn-link" onClick={() => onClose({ hideForever: true })} data-testid="tour-hide-forever"><EyeOff size={14} /> Don't show tours again</button>
            </div>
          </div>
        </div>
      )}
      {phase === 'step' && step && (
        <>
          <div className="tour-dim" onClick={() => onClose()} />
          {rect && <div className="tour-hole" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />}
          {tip && (
            <div className={`tour-card tour-tip tour-arrow-${tip.arrow}`} style={{ top: tip.top, left: tip.left, width: tip.width, '--arrow-x': `${tip.arrowX}px` }} data-testid="tour-step">
              <button type="button" className="tour-close" onClick={() => onClose()} aria-label="Close tour" data-testid="tour-close"><X size={16} /></button>
              <div className="tour-eyebrow">{doneCount} of {remaining}</div>
              <h4>{step.title}</h4>
              <p>{step.body}</p>
              <div className="tour-actions">
                <button type="button" className="tour-btn tour-btn-ghost" onClick={() => go(i - 1, -1)} data-testid="tour-back"><ChevronLeft size={16} /> Back</button>
                <button type="button" className="tour-btn tour-btn-primary" onClick={() => go(i + 1, 1)} data-testid="tour-next">{doneCount >= remaining ? 'Done' : 'Next'} {doneCount < remaining && <ChevronRight size={16} />}</button>
                <button type="button" className="tour-btn tour-btn-link" onClick={() => onClose({ hideForever: true })} data-testid="tour-hide-forever"><EyeOff size={14} /> Hide forever</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>,
    document.body,
  );
}
